import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, resolveTenantSlug, ensureCustomDomainTenantResolved } from "../lib/api";
import { applyThemePreset } from "../lib/themes";
import { onAppResume } from "../lib/capacitor";
import OfflineGate from "../components/OfflineGate";

const TenantCtx = createContext({ tenant: null, loading: true, lang: "en", setLang: () => {} });

function applyTheme(branding) {
    if (!branding) return;
    const root = document.documentElement;
    // Apply preset (color tokens + dark/light mode + Google Fonts + optional HEX overlay).
    applyThemePreset(branding.theme_preset || "bizil-green", {
        body_font: branding.body_font,
        heading_font: branding.heading_font,
        custom_primary_hex: branding.custom_primary_hex,
    });
    // Legacy primary_hsl override (kept for back-compat with already-saved tenants).
    if (!branding.custom_primary_hex && branding.primary_hsl && branding.theme_preset === "bizil-green") {
        root.style.setProperty("--primary", branding.primary_hsl);
    }
    if (branding.app_name) document.title = branding.app_name;
    const link = document.querySelector("link[rel='manifest']");
    if (link && !window.location.pathname.startsWith("/rider")) {
        link.setAttribute("href", `${process.env.REACT_APP_BACKEND_URL}/api/tenant/manifest/${resolveTenantSlug()}`);
    }
    // iter134d1 — brand the inline preload splash (defined in
    // ``public/index.html``) BEFORE removing it, so the last frame
    // the user sees matches the tenant's colours + logo instead of
    // the neutral loading spinner. Then fade + remove so React's
    // real UI can take the pixel-level spotlight.
    const splash = document.getElementById("preload-splash");
    if (splash) {
        const bg = branding.background_color || branding.custom_primary_hex;
        if (bg) splash.style.setProperty("--brand-bg", bg);
        if (branding.logo_url) {
            // Replace the neutral SVG spinner with the tenant's
            // actual logo — same behaviour Android/iOS native splash
            // exhibit, so the web loading state visually matches.
            // Keeps the "Loading, please wait…" <p> (id'd so we can
            // re-find it after the innerHTML swap) instead of wiping
            // it — this state is only on-screen for a single
            // requestAnimationFrame tick before the fade-out below
            // starts, but there's no reason to blank the label for
            // that instant when keeping it is a one-line change.
            const label = splash.querySelector("#preload-splash-text");
            splash.innerHTML = `<img src="${branding.logo_url}" alt="${branding.app_name || "Loading"}" onerror="this.remove()" />`;
            if (label) splash.appendChild(label);
        }
        // Small delay lets React finish its first mount before we
        // yank the splash — otherwise there's a visible flash of
        // unstyled routes for slow devices.
        requestAnimationFrame(() => {
            splash.classList.add("fade-out");
            setTimeout(() => splash.remove(), 320);
        });
    }
}

export function TenantProvider({ children }) {
    const [tenant, setTenant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);     // { status, slug } when lookup fails
    // Boot never reached the server. Set ONLY by the initial boot (and a failed retry of it), so it can never
    // take over an app that has already loaded — see OfflineGate's docstring.
    const [bootOffline, setBootOffline] = useState(false);
    const [lang, setLang] = useState(() => localStorage.getItem("bizil_lang") || "en");

    /** The boot fetch, shared by the first attempt and every retry. Resolves true once the tenant is loaded. */
    const loadTenant = useCallback(async () => {
        // Custom domains don't parse as ``<slug>.<platform-apex>`` — resolve
        // them via a same-origin backend lookup BEFORE computing the slug,
        // so the persisted-slug step below (resolveTenantSlug's step 4)
        // picks it up instead of falling through to the default tenant.
        await ensureCustomDomainTenantResolved();
        const slug = resolveTenantSlug();
        try {
            const { data } = await api.get(`/tenant/by-slug/${slug}`);
            setTenant(data);
            applyTheme(data?.branding);
            setError(null);
            setBootOffline(false);
            return true;
        } catch (e) {
            // Strict tenant resolution: backend now returns 404 (unknown
            // slug), 423 (suspended) or 410 (deleted) instead of falling
            // back to the default tenant. Surface that as a clear screen
            // so we don't render the default tenant's data under the
            // wrong slug (cross-tenant leak).
            const status = e?.response?.status || 0;
            if ([404, 410, 423].includes(status)) {
                setError({ status, slug });
            } else if (!e?.response) {
                // No response at all — the request never reached us (airplane mode, dead Wi-Fi, DNS failure,
                // timeout). THAT is an offline boot. A 5xx is a backend outage and deliberately does NOT land
                // here: telling someone their internet is down when it isn't sends them to reboot their router.
                setBootOffline(true);
            } else {
                console.error("tenant load failed", e);
            }
            return false;
        } finally {
            setLoading(false);
            // iter134d1 — safety net: if applyTheme() was never called
            // (tenant lookup 404/423/410/network error, so branding is
            // unavailable) the inline preload splash would linger and
            // block the error screen. Force-fade it here after the
            // loading state resolves in any way.
            const splash = document.getElementById("preload-splash");
            if (splash) {
                splash.classList.add("fade-out");
                setTimeout(() => splash.remove(), 320);
            }
        }
    }, []);

    useEffect(() => { loadTenant(); }, [loadTenant]);

    // Recover by itself once connectivity comes back, and when the user returns to the app. Only while the gate
    // is showing — no polling, no retry loop, and nothing at all once the app has booted.
    useEffect(() => {
        if (!bootOffline) return undefined;
        const retry = () => { loadTenant(); };
        window.addEventListener("online", retry);
        let unsubscribe = () => {};
        let unmounted = false;
        onAppResume(retry).then((off) => { if (unmounted) off(); else unsubscribe = off; });
        return () => {
            unmounted = true;
            unsubscribe();
            window.removeEventListener("online", retry);
        };
    }, [bootOffline, loadTenant]);

    useEffect(() => {
        localStorage.setItem("bizil_lang", lang);
    }, [lang]);

    /** Re-read the tenant doc without a page reload. Used after a setting is saved (notification rules, for
     * instance) so the surfaces that read it — the order-alert host, the merchant and rider refreshes — pick the
     * change up in the same session. Silent on failure: the previously loaded tenant stays. */
    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get(`/tenant/by-slug/${resolveTenantSlug()}`);
            setTenant(data);
            // Branding can change in the same breath as the setting that prompted the refresh, and the theme is
            // only ever applied from here — without this the logo and colours would stay on the previous
            // tenant doc until a full reload.
            applyTheme(data?.branding);
            return data;
        } catch {
            return null;
        }
    }, []);

    // Boot failed at the transport layer: show the offline screen instead of an unbranded, empty app whose
    // guarded routes bounce to /login. Retry re-runs the boot in place — same route, same React tree, no
    // reload — and authentication is never touched, so reconnecting costs no OTP.
    if (bootOffline) return <OfflineGate onRetry={loadTenant} />;

    return <TenantCtx.Provider value={{ tenant, loading, error, lang, setLang, applyTheme, refresh }}>{children}</TenantCtx.Provider>;
}

export const useTenant = () => useContext(TenantCtx);
